import { atom, getDefaultStore, useAtom, useAtomValue, useSetAtom } from 'jotai';
import { atomFamily, atomWithStorage } from 'jotai/utils';
import { ReactNode, useContext } from 'react';
import { GroupId, LayoutActions, LayoutActionsContext } from './types';

/**
 * Access the host workspace's layout actions (e.g. to open a new widget
 * instance from inside another widget). Throws if no provider is mounted —
 * widgets are always rendered inside WorkspaceLayout in practice.
 */
export const useLayoutActions = (): LayoutActions => {
  const ctx = useContext(LayoutActionsContext);
  if (!ctx) {
    throw new Error(
      'useLayoutActions must be used within a LayoutActionsContext.Provider (provided by WorkspaceLayout)'
    );
  }
  return ctx;
};

/**
 * Atom family for widget toolbar controls
 */
const widgetControlAtomFamily = atomFamily((_instanceId: string) =>
  atom<ReactNode>(undefined)
);

/**
 * Hook to set widget control (toolbar buttons/actions)
 * Use in widget to register controls
 */
export const useSetWidgetControl = (instanceId: string) => {
  return useSetAtom(widgetControlAtomFamily(instanceId));
};

/**
 * Hook to get widget control
 * Use in layout system to render controls
 */
export const useWidgetControl = (instanceId: string) => {
  return useAtomValue(widgetControlAtomFamily(instanceId));
};

/**
 * Atom family for widget settings panels
 */
const widgetSettingsAtomFamily = atomFamily((_instanceId: string) =>
  atom<ReactNode>(undefined)
);

/**
 * Hook to set widget settings panel
 * Use in widget to provide settings UI
 */
export const useSetWidgetSettings = (instanceId: string) => {
  return useSetAtom(widgetSettingsAtomFamily(instanceId));
};

/**
 * Hook to get widget settings
 * Use in layout system to render settings
 */
export const useWidgetSettings = (instanceId: string) => {
  return useAtomValue(widgetSettingsAtomFamily(instanceId));
};

/**
 * Atom family for widget context menu
 */
const widgetMenuAtomFamily = atomFamily((_instanceId: string) =>
  atom<ReactNode>(undefined)
);

/**
 * Hook to set widget context menu
 */
export const useSetWidgetMenu = (instanceId: string) => {
  return useSetAtom(widgetMenuAtomFamily(instanceId));
};

/**
 * Hook to get widget context menu
 */
export const useWidgetMenu = (instanceId: string) => {
  return useAtomValue(widgetMenuAtomFamily(instanceId));
};

/**
 * Storage key for a widget instance's group assignment.
 * One key per tab id — survives reload, matches StudioV2's
 * `studio-workspaces:linking:{tabId}:group` convention.
 */
const widgetGroupStorageKey = (instanceId: string) =>
  `pulse-widget-group:${instanceId}`;

/**
 * Atom family for widget grouping. Linked widgets share the same groupId.
 * Persisted per-instance to localStorage so a user's link choices survive reload.
 */
const widgetGroupAtomFamily = atomFamily((instanceId: string) =>
  atomWithStorage<GroupId | undefined>(widgetGroupStorageKey(instanceId), undefined)
);

/**
 * Hook to manage widget group (read/write)
 */
export const useWidgetGroup = (instanceId: string) => {
  return useAtom(widgetGroupAtomFamily(instanceId));
};

/**
 * Hook to get widget group ID (read-only)
 */
export const useWidgetGroupId = (instanceId: string) => {
  return useAtomValue(widgetGroupAtomFamily(instanceId));
};

/**
 * Hook to set widget group ID (write-only)
 */
export const useSetWidgetGroupId = (instanceId: string) => {
  return useSetAtom(widgetGroupAtomFamily(instanceId));
};

/**
 * Imperatively set a widget's group from outside React (e.g. when applying a preset).
 * Writes to the same atom family as the hooks, so subscribed widgets re-render.
 */
export const setWidgetGroupIdImperative = (
  instanceId: string,
  groupId: GroupId | undefined
): void => {
  getDefaultStore().set(widgetGroupAtomFamily(instanceId), groupId);
};
